/**
 * Mindhunters Design Tokens - Single Source of Truth
 *
 * Authority: contracts/design/MH_DESIGN_V4_FOG.md
 * Palette: Fog Oyster + Espresso accent
 */

export const T = {
  // Backgrounds (Fog Oyster)
  bg0: '#F4F5F7',
  bg1: '#EAEBEE',
  s1: '#E0E1E4',
  s2: '#FFFFFF',
  s3: '#EAEBEE',
  card: '#FFFFFF',
  /** Canon primitive tokens (see docs/canon-jsx/canon/hunter/primitives/tokens.jsx). */
  surface2: '#F9FAFB',

  // Borders
  border: 'rgba(0,0,0,0.06)',
  borderStrong: 'rgba(0,0,0,0.12)',
  cardBorder: 'rgba(0,0,0,0.06)',
  /** Canon primitives: mid-strength border used for user bubble + composer rails. */
  borderMd: 'rgba(0,0,0,0.10)',
  /** Canon primitives: emphasis border used for sharpened rails. */
  borderBr: 'rgba(0,0,0,0.14)',

  // Text
  text1: '#0F1011',
  text2: 'rgba(15,16,17,0.62)',
  text3: 'rgba(15,16,17,0.44)',
  /** Design canon hint token. */
  textMuted: 'rgba(15,16,17,0.28)',
  textM: 'rgba(15,16,17,0.28)',
  /** Canon primitive timestamp / quiet metadata color (see primitives/tokens.jsx). */
  text4: 'rgba(15,16,17,0.24)',

  // Accent - graphite buttons (semantic aliases preserved)
  accent: '#0F1011',
  accentText: '#F4F5F7',
  accentMuted: 'rgba(15,16,17,0.04)',
  /** Canon: primary-button background. */
  graphite: '#0F1011',
  /** Canon: primary-button foreground. */
  graphiteOn: '#F4F5F7',

  // Espresso - identity accent
  espresso: '#8B6F4E',
  espressoMuted: 'rgba(139,111,78,0.12)',
  espressoSubtle: 'rgba(139,111,78,0.04)',
  espressoSoft: 'rgba(139,111,78,0.50)',
  espressoGlow: 'rgba(139,111,78,0.06)',
  /** Canon primitive: espresso card tint / hover surface. */
  espressoDim: 'rgba(139,111,78,0.04)',
  /** Canon primitive: espresso border on signal / live cards. */
  espressoBrd: 'rgba(139,111,78,0.10)',

  // Functional
  success: '#0D8C5B',
  successMuted: 'rgba(13,140,91,0.20)',
  warning: '#D4A017',
  error: '#C4342E',
  orange: '#D4A017',

  /** Canon HOME mockup: warning-tone card tint (partial_failure, blocker cards). */
  tintWarning: 'rgba(212,160,23,0.15)',
  /** Canon HOME mockup: error-tone card tint (error card border). */
  tintError: 'rgba(196,52,46,0.12)',

  /** Canon HOME mockup: modal / sheet scrim background. */
  scrim: 'rgba(15,16,17,0.32)',
  /** Canon HOME mockup: lighter scrim for non-blocking overlays. */
  scrimLight: 'rgba(15,16,17,0.12)',

  // Shadows
  shadowSm: '0 1px 2px rgba(0,0,0,0.03), 0 0 0 1px rgba(0,0,0,0.02)',
  shadowMd: '0 2px 8px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.03)',
  shadowLg: '0 8px 24px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)',
  shadowHero: '0 20px 60px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.04)',
  /** Canon primitive card shadow. */
  shadowCard: '0 1px 3px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
  /** Canon HOME mockup: primary (graphite) button shadow. */
  shadowBtnPrimary: '0 1px 4px rgba(15,16,17,0.15)',
  /** Canon HOME mockup: mobile drawer right-edge shadow. */
  shadowDrawer: '4px 0 20px rgba(0,0,0,0.06)',
  /** Canon HOME mockup: bottom sheet top-edge shadow. */
  shadowSheet: '0 -4px 20px rgba(0,0,0,0.06)',
  /** Canon HOME mockup: device frame / expanded composer modal shadow. */
  shadowDevice: '0 10px 40px rgba(0,0,0,0.08)',

  // Legacy compat
  r: { sm: 4, md: 6, lg: 8, xl: 10 },
  font: "'Inter', system-ui, sans-serif",
  fontFamily: {
    sans: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    serif: "'Fraunces', Georgia, serif",
  },
  mono: "'Inter', system-ui, sans-serif",
} as const;

/**
 * Canon shimmer gradient used on HunterLabel + live card top bar.
 * Verbatim from `docs/canon-jsx/canon/hunter/primitives/tokens.jsx`.
 */
export const shimmerGradient =
  'linear-gradient(110deg, #8B6F4E 20%, #C4A882 45%, #E8D5B8 50%, #C4A882 55%, #8B6F4E 80%)';

/** Canon font stack. */
export const canonFontStack =
  "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const tokens = {
  color: {
    ...T,
    bg: T.bg0,
    bg2: T.bg1,
    surface: T.card,
    // `surface2` in repo-tokens deliberately mirrors canon primitive `surface2`
    // (`#F9FAFB`) used on desktop composer tray + quiet surfaces.
    surface2: T.surface2,
    elevated: T.bg1,
    // Canon primitive borders supersede the design-doc `borderStrong` alias
    // so inline call sites (`tokens.color.borderMd`, `tokens.color.borderBr`)
    // resolve to the exact canon opacities.
    borderMd: T.borderMd,
    borderBr: T.borderBr,
    t1: T.text1,
    t2: T.text2,
    t3: T.text3,
    t4: T.textMuted,
    accentBg: T.accentText,
  },
  radius: {
    card: 12,
    button: 8,
    input: 14,
    holder: 10,
    chip: 8,
  },
  spacing: {
    xs: 4,
    sm: 6,
    md: 10,
    lg: 14,
    xl: 20,
  },
  sectionSpacing: {
    dense: 16,
    normal: 32,
    comfortable: 48,
    spacious: 64,
    brand: 96,
    hero: 120,
  },
  sectionPadding: {
    operational: 16,
    product: 32,
    brand: 96,
    hero: 120,
  },
} as const;

export type TokenKey = keyof typeof T;
export type TokenValue = (typeof T)[TokenKey];

/** Backwards compatibility bridge. */
export const t = T;

/**
 * Token-integrity invariant (PR5 hardening).
 *
 * The runtime palette is Fog Oyster + Espresso accent (see
 * contracts/design/MH_DESIGN_V4_FOG.md). Teal/Graphite hexes from the
 * deprecated v3 palette MUST never ship in tokens.
 *
 * The canonical forbidden hex list lives in
 * `lib/design/token-integrity.ts` so this source file can remain a clean
 * single-source-of-truth for the palette. The integrity guard wires up
 * a compile-time type assertion + a dev-only runtime warning when this
 * module is imported.
 */
import { assertNoForbiddenTokenHexes } from './token-integrity';

assertNoForbiddenTokenHexes(T);
